import { useState, useEffect, useCallback } from 'react'
import { siteMembersApi, ApiSiteMember } from '../utils/api'

interface Props {
  siteId: string
  siteName: string
  onClose: () => void
}

export function ShareModal({ siteId, siteName, onClose }: Props) {
  const [members, setMembers] = useState<ApiSiteMember[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'EDITOR' | 'VIEWER'>('EDITOR')
  const [inviting, setInviting] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const editorBase = window.location.origin

  const load = useCallback(async () => {
    setLoading(true)
    try { setMembers(await siteMembersApi.list(siteId)) }
    catch { setMembers([]) }
    finally { setLoading(false) }
  }, [siteId])

  useEffect(() => { load() }, [load])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setInviting(true)
    try {
      const member = await siteMembersApi.invite(siteId, email.trim(), role)
      setMembers(prev => [member, ...prev])
      setEmail('')
    } catch (err) {
      alert('Ошибка: ' + (err instanceof Error ? err.message : 'Не удалось пригласить'))
    } finally {
      setInviting(false)
    }
  }

  const handleRemove = async (memberId: string) => {
    if (!confirm('Удалить участника?')) return
    await siteMembersApi.remove(siteId, memberId).catch(() => null)
    setMembers(prev => prev.filter(m => m.id !== memberId))
  }

  const copyInviteLink = async (member: ApiSiteMember) => {
    if (!member.inviteToken) return
    const link = `${editorBase}/join/${member.inviteToken}`
    await navigator.clipboard.writeText(link).catch(() => null)
    setCopiedId(member.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '12px', width: '480px', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>Совместный доступ</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{siteName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--text-muted)', lineHeight: 1, padding: '4px' }}>×</button>
        </div>

        {/* Invite form */}
        <form onSubmit={handleInvite} style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>Пригласить участника</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="email@example.com"
              required
              style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '13px', background: 'var(--bg-2)', color: 'var(--text-primary)', outline: 'none' }}
            />
            <select
              value={role}
              onChange={e => setRole(e.target.value as 'EDITOR' | 'VIEWER')}
              style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '13px', background: 'var(--bg-2)', color: 'var(--text-primary)', cursor: 'pointer' }}
            >
              <option value="EDITOR">Редактор</option>
              <option value="VIEWER">Просмотр</option>
            </select>
            <button
              type="submit"
              disabled={inviting}
              style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {inviting ? '...' : 'Пригласить'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: 1.4 }}>
            Скопируйте ссылку приглашения и отправьте её участнику. Участник должен быть зарегистрирован на BST.
          </div>
        </form>

        {/* Members list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>Загрузка...</div>
          ) : members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>👥</div>
              Нет приглашённых участников
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {members.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: m.accepted ? 'var(--accent)' : 'var(--bg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: m.accepted ? '#fff' : 'var(--text-muted)', flexShrink: 0 }}>
                    {m.email.slice(0, 1).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {m.role === 'EDITOR' ? '✏ Редактор' : '👁 Просмотр'}
                      {' · '}
                      {m.accepted ? <span style={{ color: '#10b981' }}>✓ Принято</span> : <span style={{ color: '#f59e0b' }}>⏳ Ожидает</span>}
                    </div>
                  </div>
                  {!m.accepted && m.inviteToken && (
                    <button
                      onClick={() => copyInviteLink(m)}
                      title="Скопировать ссылку"
                      style={{ padding: '4px 8px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
                    >
                      {copiedId === m.id ? '✓ Скопировано' : '🔗 Ссылка'}
                    </button>
                  )}
                  <button
                    onClick={() => handleRemove(m.id)}
                    title="Удалить"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '16px', lineHeight: 1, padding: '4px', flexShrink: 0 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
